import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress, Box, Button, Typography, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { listDatasourcesForChannel, type DatasourceOption } from '../../../services/datasourceLookupService';

// Same storage_type category labels as the Data Sources panel's own icon mapping
// (dataSourceUtils.tsx getDataSourceIcon) — kept as a small local label map here since this
// picker only needs text, not the full icon set, and dataSourceUtils.tsx isn't a labels module.
const STORAGE_TYPE_LABELS: Record<string, string> = {
  database: 'Database',
  cloud: 'Cloud storage',
  app: 'App',
  website: 'Website',
  local: 'File',
};
const storageTypeLabel = (storageType: string | undefined): string | null =>
  (storageType && STORAGE_TYPE_LABELS[storageType]) || null;

// PR #680 review (Low) — explains *why* the option can't be picked right now, per reason, rather
// than one hardcoded "Still processing" string that was wrong for a failed item.
const NOT_READY_TOOLTIPS: Record<string, string> = {
  'Processing…': 'Still processing — not selectable yet',
  'Scraping…': 'Still being scraped — not selectable yet',
  'Failed to process': 'Failed to process — fix and reprocess before selecting',
};
const notReadyTooltipText = (reason: string | undefined): string =>
  (reason && NOT_READY_TOOLTIPS[reason]) || 'Not selectable yet';

// Same small info-icon-with-tooltip pattern already used in WorkflowInspectorPanel.tsx,
// WorkflowToolbar.tsx and WorkflowCanvasTab.tsx — kept as a local copy here too rather than a
// shared export, matching how those files each define their own copy.
const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
  <Tooltip
    title={<Box component="span" sx={{ whiteSpace: 'pre-line', display: 'block', fontSize: '0.75rem' }}>{text}</Box>}
    placement="top"
    arrow
    slotProps={{ tooltip: { sx: { maxWidth: 360 } } }}
  >
    <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }} tabIndex={-1}>
      <InfoOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  </Tooltip>
);

export interface DynamicMultiselectFieldProps {
  label: string;
  required?: boolean;
  /** Which catalog `source.endpoint` this field descriptor named (workflowGraphTypes.ts
   * CatalogActionInput.source) — read instead of hardcoding a URL, per spec §3.1. Only
   * 'datasources' is wired to a real backend today; anything else renders a clear
   * "not supported yet" state rather than guessing a URL. */
  endpoint?: string;
  /** The enclosing channel this field is scoped to — see DynamicMultiselectField's own doc
   * comment below for why this is an ambient prop rather than a sibling form field here. */
  channelId?: string;
  /** Selected option ids only — dynamic_multiselect_FE_datasource_picker_spec §3.4. */
  value: string[];
  onChange: (ids: string[]) => void;
}

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;
const SUPPORTED_ENDPOINTS = new Set(['datasources']);

/**
 * dynamic_multiselect_FE_datasource_picker_spec.md — new async field-type component (§4). Unlike
 * every other catalog field type, this one has no options at catalog-load time: it fetches live
 * from BS, filtered by channel + live search, with its own loading/error/empty states, debounce,
 * request cancellation, and pagination.
 *
 * §2.1 ("channel selector") adaptation: this spec assumes a channel-picker control sits beside
 * the field and can change at any time, requiring a "badge a stale selection" treatment for
 * chips from a channel the user switched away from. In this codebase's actual integration point
 * (a node's inspector panel inside one channel's Settings drawer), there is no channel-switcher
 * control at all — `channelId` is fixed, ambient context for the whole panel, not a sibling form
 * field. The stale-selection badge therefore doesn't apply here; there's nothing to switch away
 * from within one node's inspector. Everything else in the spec (debounce, cancellation, loading/
 * error/empty states, pagination, pinned selections, per-(channel,search) caching) is implemented
 * as described.
 */
const DynamicMultiselectField: React.FC<DynamicMultiselectFieldProps> = ({
  label,
  required,
  endpoint,
  channelId,
  value,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<DatasourceOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // §3.4 — resolves a chip's label (and now its storage type/readiness) even once it's fallen
  // out of the current page/search scope; falls back to the raw id (at render time below) if
  // truly never seen this session. Caches the full option, not just the label, so an already-
  // selected datasource still shows its real type/readiness even once paged/searched away.
  const optionCacheRef = useRef<Map<string, DatasourceOption>>(new Map());
  // §2.3 — race-condition guard: a stale response (superseded by a newer keystroke) is dropped
  // even if it resolves later. AbortController also actually cancels the in-flight request.
  const abortRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // §4 — cache per (channel, search, page); cleared whenever channel changes (never shared
  // across channels), lifetime of this mount only (not persisted across reloads).
  const resultCacheRef = useRef<Map<string, { items: DatasourceOption[]; hasMore: boolean }>>(new Map());

  const endpointSupported = !endpoint || SUPPORTED_ENDPOINTS.has(endpoint);

  const runFetch = (search: string, pageToFetch: number, append: boolean) => {
    if (!channelId || !endpointSupported) return;
    // PR #669 review (N1) — abort + bump the sequence FIRST, before the cache check, on every
    // call. Previously a cache hit returned early without either, so an in-flight request from a
    // moment ago (e.g. typing "a") could still resolve afterward, pass its own now-stale
    // `seq !== requestSeqRef.current` check (since nothing had bumped it), and overwrite the
    // correct cached result with stale ones.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeqRef.current;

    const cacheKey = `${channelId}::${search}::${pageToFetch}`;
    const cached = resultCacheRef.current.get(cacheKey);
    if (cached) {
      cached.items.forEach((o) => optionCacheRef.current.set(o.id, o));
      setOptions((prev) => (append ? [...prev, ...cached.items] : cached.items));
      setHasMore(cached.hasMore);
      setError(null);
      // PR #669 review (M4) — the N1 fix's abort+bump above happens before this cache check now,
      // but this early return still skipped clearing loading state, since nothing here ever set
      // it false on a hit. An aborted previous request's own .catch/.finally both exit early on
      // the now-stale seq, so without this, a cache hit left the spinner (and the "No datasources
      // found" empty state, which needs !loading) stuck until the next uncached fetch resolved.
      setLoading(false);
      setLoadingMore(false);
      return;
    }

    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);

    listDatasourcesForChannel(channelId, { search, page: pageToFetch, size: PAGE_SIZE, signal: controller.signal })
      .then((result) => {
        if (seq !== requestSeqRef.current) return; // superseded — §2.3
        result.items.forEach((o) => optionCacheRef.current.set(o.id, o));
        // PR #680 review (Low) — a not-ready item's status can change at any time (processing
        // finishes, a retry succeeds); caching a page that contains one meant it kept showing the
        // same stale badge until the channel changed. Only a page where every item is already
        // ready is safe to reuse verbatim — anything else is re-fetched fresh next time.
        if (result.items.every((o) => o.ready)) {
          resultCacheRef.current.set(cacheKey, result);
        }
        setOptions((prev) => (append ? [...prev, ...result.items] : result.items));
        setHasMore(result.hasMore);
      })
      .catch((err: unknown) => {
        if (seq !== requestSeqRef.current) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load datasources');
      })
      .finally(() => {
        if (seq !== requestSeqRef.current) return;
        setLoading(false);
        setLoadingMore(false);
      });
  };

  // §2.2/§2.3 — fires on open and on every debounced keystroke; always resets to page 1 (a fresh
  // search is a new result set — "load more" below is the only path that increments page).
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      runFetch(inputValue, 1, false);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, inputValue, channelId]);

  // §4 — a channel change is a fully separate cache namespace; also drops stale options/paging
  // left over from the previous channel instead of flashing them under the new one.
  useEffect(() => {
    resultCacheRef.current.clear();
    optionCacheRef.current.clear();
    setOptions([]);
    setHasMore(false);
    setPage(1);
  }, [channelId]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    runFetch(inputValue, nextPage, true);
  };

  // §3.4 — selected ids may not be in `options` (different search/page); synthesize a stable
  // option for each from the option cache, falling back to the raw id. A selection never seen
  // this session (e.g. a saved workflow reopened) defaults to ready: true rather than flagging
  // it "not ready" on no evidence either way — this cache is display-only, never re-validated
  // against the backend, so it must never block re-saving an existing, already-valid selection.
  const selectedOptions: DatasourceOption[] = value.map(
    (id) => optionCacheRef.current.get(id) ?? { id, label: id, ready: true }
  );

  // §2.3 — keep selected items visible/pinned even when the current search would otherwise
  // exclude them from the rendered list.
  const mergedOptions: DatasourceOption[] = [
    ...selectedOptions,
    ...options.filter((o) => !value.includes(o.id)),
  ];

  let emptyStateContent: React.ReactNode = 'Type to search';
  if (!channelId) {
    emptyStateContent = 'No channel context available for this field.';
  } else if (!endpointSupported) {
    emptyStateContent = `"${endpoint}" isn't a supported datasource yet.`;
  } else if (error) {
    emptyStateContent = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="error">{error}</Typography>
        <Button size="small" onClick={() => runFetch(inputValue, 1, false)} sx={{ textTransform: 'none' }}>
          Retry
        </Button>
      </Box>
    );
  } else if (!loading && options.length === 0) {
    emptyStateContent = 'No datasources found for this channel.';
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Autocomplete
        multiple
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        options={mergedOptions}
        value={selectedOptions}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, val) => option.id === val.id}
        // Only blocks picking a NEW not-ready datasource — an already-selected one (e.g. from a
        // previously saved workflow) must stay removable from the list regardless of its current
        // readiness, never silently locked in.
        getOptionDisabled={(option) => !option.ready && !value.includes(option.id)}
        onChange={(_e, newValue) => onChange(newValue.map((o) => o.id))}
        // Multi-select means picking one item is rarely the whole task — without this, MUI's
        // default behavior closes the dropdown after every single selection, forcing a reopen
        // (losing the current search) to tick the next one.
        disableCloseOnSelect
        inputValue={inputValue}
        onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
        disabled={!channelId || !endpointSupported}
        loading={loading}
        // Server-side filtering only — never re-filter the already-scoped response client-side.
        filterOptions={(opts) => opts}
        noOptionsText={emptyStateContent}
        ListboxProps={{
          onScroll: (event: React.UIEvent<HTMLUListElement>) => {
            const el = event.currentTarget;
            if (hasMore && !loadingMore && el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
              handleLoadMore();
            }
          },
        }}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip label={option.label} {...getTagProps({ index })} key={option.id} size="small" />
          ))
        }
        renderOption={(props, option) => {
          const typeLabel = storageTypeLabel(option.storageType);
          const isDisabled = !option.ready && !value.includes(option.id);
          const optionContent = (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
              <Typography
                variant="body2"
                sx={{ flex: 1, color: isDisabled ? 'text.disabled' : 'text.primary' }}
              >
                {option.label}
              </Typography>
              {typeLabel && (
                <Chip
                  label={typeLabel}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              )}
              {!option.ready && (() => {
                const notReadyBadge = (
                  <Chip
                    label={option.notReadyReason ?? 'Not ready yet'}
                    size="small"
                    color={option.notReadyReason === 'Failed to process' ? 'error' : 'warning'}
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                );
                // PR #680 re-review (Low) — an already-selected not-ready item is still enabled
                // and removable (getOptionDisabled exempts it), so a "not selectable yet" tooltip
                // on it was misleading. Only a genuinely disabled option gets the tooltip; a
                // removable one just shows the status badge on its own.
                if (!isDisabled) return notReadyBadge;
                return (
                  // The disabled <li>'s own CSS sets pointer-events: none (MUI's aria-disabled
                  // rule), which a plain child Tooltip never receives hover through.
                  // pointerEvents: 'auto' here opts just this badge back in.
                  <Tooltip title={notReadyTooltipText(option.notReadyReason)} placement="right">
                    <Box sx={{ display: 'inline-flex', pointerEvents: 'auto' }}>{notReadyBadge}</Box>
                  </Tooltip>
                );
              })()}
            </Box>
          );
          // MUI's own `getOptionDisabled` only paints `aria-disabled` + CSS (pointer-events: none)
          // on the option — its mouse click handler runs regardless (unlike its Enter-key path,
          // which does check disabled). Guarding here too means a disabled option can never be
          // picked via click, not just styled as if it can't.
          const { onClick: muiOnClick, ...restProps } = props;
          return (
            <li
              {...restProps}
              onClick={isDisabled ? undefined : muiOnClick}
              key={option.id}
            >
              {optionContent}
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={required ? `${label} *` : label}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={16} /> : null}
                  <FieldHelp text="Searches datasources connected to this channel as you type. Scroll down in the list to load more results." />
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {loadingMore && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Loading more…
        </Typography>
      )}
    </Box>
  );
};

export default DynamicMultiselectField;
